import React, { FC, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { useTranslation } from 'react-i18next'
import Modal from '../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'

interface MemberDetail {
  id: number
  name: string
  kana: string | null
  profile_image: string | null
}

interface ApproverMemberDetail extends MemberDetail {
  priority: number
}

interface ApprovalGroup {
  id: number
  name: string
  description: string | null
  created_by: string | null
  approve_members: {
    id: number
    priority: number
    name: string
    kana: string | null
    profile_image: string | null
  }[]
  request_members: MemberDetail[]
  viewable_members: MemberDetail[]
}

interface ModalState {
  display: boolean
  targetMember: MemberDetail | null
  targetData: ApprovalGroup | null
  approvalGroupList: ApprovalGroup[]
}

interface Props {
  modalState: ModalState
  closeCallback: VoidFunction
  updateCallback: (approval_group_id: number | null, member_id: number) => void
}

const ApproverSettingForMemberModal: FC<Props> = (props) => {
  const { t } = useTranslation()

  const {
    commonPart,
  } = useSelector((state: RootState) => state)

  const {
    modalState,
    closeCallback,
    updateCallback,
  } = props

  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [selectApprovalGroupId, setSelectApprovalGroupId] = useState<number | null>(null)
  const [displaySelectBox, setDisplaySelectBox] = useState<boolean>(false)
  const [error, setError] = useState<string>("")

  const buildApprovalFlow = (list: ApproverMemberDetail[]) => {
    if (!list.length) return (<>承認フローが設定されていません！</>)
    return (
      <>
        {
          list.map((member, i) => (
            <React.Fragment key={uniqueId()}>
              <FontAwesomeIcon icon={['fas', 'arrow-right']} />
              <div className="approver_block">
                <div className="approver_block_title">
                  {list.length === i + 1 ? "最終" : `${i + 1}次`}
                  承認者
                </div>
                <div className="approver_block_name">
                  <img
                    alt="profile"
                    src={member.profile_image ? member.profile_image : `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                    className="profile_icon"
                  />
                  <ruby>
                    {member.name}
                    <rt>{member.kana}</rt>
                  </ruby>
                </div>
              </div>
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const clear = () => {
    closeCallback()
    setDisplaySelectBox(false)
    setError("")
  }

  const selectApprovalGroup = (approvalGroupId: number | null) => {
    setSelectApprovalGroupId(approvalGroupId)
    setDisplaySelectBox(false)
    if (!approvalGroupId) {
      setError("")
      return
    }
    const targetApprovalGroup = modalState.approvalGroupList.find((v) => v.id === approvalGroupId)
    if (!targetApprovalGroup) {
      setError("")
      return
    }
    setError("")
  }

  const saveApprovalGroup = () => {
    if (!commonPart.company?.id) return
    updateCallback(selectApprovalGroupId, modalState.targetMember?.id || 0)
  }

  useEffect(() => {
    setDisplayModal(modalState.display)
    setSelectApprovalGroupId(modalState.targetData?.id || 0)
  }, [modalState.display])

  return (
    <>
      <Modal
        title="承認グループ設定"
        show={displayModal}
        wrapperClass="approver_setting_modal_for_member w750"
        closeCallback={clear}
        closeable
      >
        <>
          <div className="description">
            <div>
              {`${modalState.targetMember?.name}さんの承認グループを変更します。`}
            </div>
          </div>
          <div className="item">
            <div className="title flex">
              <div className="label red">必須</div>
              承認グループ
            </div>
            <div className="detail row_1 custom_select_area">
              <div
                className="custom_select"
                onClick={() => setDisplaySelectBox(true)}
                role="none"
              >
                {modalState.approvalGroupList.find((v) => v.id === selectApprovalGroupId)?.name || "承認フローが設定されていません"}
              </div>
              {
                error && (
                  <div
                    className="error"
                    key={error}
                  >
                    {error}
                  </div>
                )
              }
              {
                displaySelectBox && (
                  <>
                    <div className="custom_select_box">
                      <div className="custom_select_box_title">
                        承認グループ
                      </div>
                      <div className="custom_select_box_contents">
                        <div
                          className="custom_select_box_row unset"
                          onClick={() => selectApprovalGroup(null)}
                          role="none"
                        >
                          承認グループを設定しない
                        </div>
                        {
                          modalState.approvalGroupList.map((approvalGroup) => (
                            <div
                              key={`${approvalGroup.name}_${approvalGroup.id}`}
                              className="custom_select_box_row"
                              onClick={() => selectApprovalGroup(approvalGroup.id)}
                              role="none"
                            >
                              <div className="custom_select_box_row_title">
                                {approvalGroup.name}
                              </div>
                              <div
                                className="custom_select_box_row_flow"
                                style={{
                                  justifyContent: approvalGroup.approve_members.length ? "left" : "center",
                                }}
                              >
                                {
                                  !!approvalGroup.approve_members.length && (
                                    <div className="approver_block">
                                      <div className="approver_block_name">
                                        <img
                                          alt="profile"
                                          src={
                                            modalState.targetMember?.profile_image
                                            || `/static/images/profile/${(Number(modalState.targetMember?.id) || 0) % 12}.jpeg`
                                          }
                                          className="profile_icon"
                                        />
                                        <ruby
                                          style={{
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {modalState.targetMember?.name}
                                          <rt>{modalState.targetMember?.kana}</rt>
                                        </ruby>
                                      </div>
                                    </div>
                                  )
                                }
                                {buildApprovalFlow(approvalGroup.approve_members || [])}
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                    <div className="close_screen" role="presentation" onClick={() => setDisplaySelectBox(false)} />
                  </>
                )
              }
            </div>
          </div>
        </>
        <>
          <button type="button" className="button cancel" onClick={clear}>
            {t('form.component.cancel')}
          </button>
          <button
            type="button"
            className="button"
            onClick={saveApprovalGroup}
            disabled={
              !!error
              || modalState.targetData?.id === selectApprovalGroupId
              || (!modalState.targetData?.id && !selectApprovalGroupId)
            }
          >
            変更
          </button>
        </>
      </Modal>
    </>
  )
}

export default ApproverSettingForMemberModal
